import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Mail, Lock, User, ArrowLeft, Loader2, Eye, EyeOff, MessageCircle, ShieldCheck, RefreshCw, ChevronDown, ChevronUp, Info, Phone, MapPin, Building2 } from 'lucide-react';
import Msg91Widget from '../components/Msg91Widget';
import { useApp } from '../context/AppContext';
import { getAttribution } from '../lib/attribution';
import { INDIAN_STATES } from '../lib/indianStates';
import { emitSignupEvent, emitSignupEventBeacon, clearSignupSessionId } from '../lib/signupFunnel';
import { BUILD_VERSION } from '../lib/buildVersion';

/**
 * Register — iter-51.19.
 *
 * Two-step widget-gated signup:
 *   1. Enter name / email / password → click "Verify mobile" → MSG91's
 *      DEFAULT UI popup opens (phone entry + OTP entry, all owned by
 *      MSG91). Nothing about the mobile touches our backend until the
 *      widget succeeds.
 *   2. On widget success we get an opaque `access_token`. We POST it
 *      to /auth/register-with-otp along with email/password/name. The
 *      backend calls MSG91's /verifyAccessToken to confirm the token
 *      (browser is never trusted).
 *
 * Iter-51.19 UX fixes:
 *   • ONE message on screen at a time — this page owns the whole
 *     status text; the widget component no longer has a 'verified'
 *     state that could contradict a parent-side error.
 *   • Force widget remount on error via `widgetKey`, so a fresh MSG91
 *     token can be issued after the user retries. Without a fresh
 *     mount MSG91's SDK holds the previous already-consumed token
 *     and the second attempt fails with the same "already used" error.
 *   • Parent-side single-fire guard (`submittingRef`) — the widget
 *     component now also guards, but redundancy is cheap and this
 *     is auth code.
 */
export default function Register() {
  const nav = useNavigate();
  const { checkAuth } = useApp();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Iter-52.25 — City + State captured at signup for Facebook-ad targeting.
  const [stateName, setStateName] = useState('');
  const [city, setCity] = useState('');
  const [locationSkipped, setLocationSkipped] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form');   // 'form' | 'widget' | 'submitting' | 'success' | 'blocked'
  const [showPassword, setShowPassword] = useState(false);
  const [dupMobile, setDupMobile] = useState(false);
  const [widgetKey, setWidgetKey] = useState(0); // bump to force widget remount
  // Iter-52.41 — gate: don't mount Msg91Widget until the user
  // explicitly taps the "verify" button. MSG91's SDK auto-opens a
  // full-screen popup the instant its `<msg91-otp-provider>` mounts,
  // which visually replaces our WhatsApp-hint before the user can
  // read it. Gating the mount behind an ack keeps the hint on-screen
  // permanently, and when the user closes MSG91's popup (or hits an
  // error) they return to the hint + button ready to relaunch.
  const [hintAcked, setHintAcked] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false); // iter-51.24h — collapsible
  // Iter-52.23 — phone-only signup mode. When true, we skip email +
  // password entirely and go straight to the MSG91 widget. Populated
  // from /api/config/support so a prod flag flip disables the button
  // without a rebuild.
  const [mode, setMode] = useState('classic'); // 'classic' | 'phone_only'
  const [phoneOnlyEnabled, setPhoneOnlyEnabled] = useState(false);
  const submittingRef = useRef(false);
  // Iter-52.32 — cache the last mobile MSG91's shadow-DOM shows the
  // user typed. Populated by Msg91Widget's polling. Used at pagehide
  // time to record "abandoned" funnel events with the mobile still
  // attached, so the Nudge button in the admin drilldown becomes
  // tappable for drop-offs where MSG91's own failure callback never
  // fires (tab closed, popup dismissed, network died mid-OTP).
  const widgetMobileRef = useRef(null);
  const widgetOpenRef = useRef(false);
  const otpSubmittedFiredRef = useRef(false);

  useEffect(() => {
    api.get('/config/support').then(({ data }) => {
      setPhoneOnlyEnabled(Boolean(data.phone_only_signup_enabled));
    }).catch(() => {});
  }, []);

  // Iter-52.32 — pagehide/visibilitychange beacon. When the widget is
  // open (user has tapped Continue-to-mobile-verification but hasn't
  // completed OTP yet) and the tab is closed or backgrounded, emit an
  // otp_submitted status=failed error=abandoned event with the last
  // mobile MSG91's shadow DOM saw. Uses sendBeacon so it survives the
  // unload. Fires ONLY if a real otp_submitted hasn't already fired
  // this session — otherwise we'd double-count successful OTPs.
  useEffect(() => {
    const flushAbandoned = () => {
      if (!widgetOpenRef.current) return;
      if (otpSubmittedFiredRef.current) return;
      otpSubmittedFiredRef.current = true;
      emitSignupEventBeacon('otp_submitted', {
        mode,
        status: 'failed',
        mobile_e164: widgetMobileRef.current || undefined,
        error: 'abandoned',
      });
    };
    const onPageHide = () => flushAbandoned();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushAbandoned();
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [mode]);

  const _validateSharedFields = () => {
    if (!name.trim()) { setError('Please enter your name.'); return false; }
    if (!locationSkipped) {
      if (!stateName) { setError('Please select your state, or tap "Skip for now".'); return false; }
      if (!city.trim()) { setError('Please enter your city, or tap "Skip for now".'); return false; }
    }
    return true;
  };

  const submitStep1 = (e) => {
    e.preventDefault();
    setError('');
    if (!_validateSharedFields()) return;
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.'); return;
    }
    setMode('classic');
    // Iter-52.26 — funnel step 1: user reached the OTP widget.
    emitSignupEvent('otp_reached', { mode: 'classic' });
    widgetOpenRef.current = true;
    otpSubmittedFiredRef.current = false;
    widgetMobileRef.current = null;
    setHintAcked(false);           // iter-52.41 — hint must be re-acked per attempt
    setStep('widget');
  };

  // Iter-52.23 — go straight to widget without email/password.
  const startPhoneOnly = () => {
    setError('');
    if (!_validateSharedFields()) return;
    setMode('phone_only');
    // Iter-52.26 — funnel step 1: user reached the OTP widget.
    emitSignupEvent('otp_reached', { mode: 'phone_only' });
    widgetOpenRef.current = true;
    otpSubmittedFiredRef.current = false;
    widgetMobileRef.current = null;
    setHintAcked(false);           // iter-52.41 — hint must be re-acked per attempt
    setStep('widget');
  };

  // Iter-52.5 — Sign-up-with-Google. The Emergent-managed Google auth
  // endpoint handles BOTH sign-in AND sign-up on the same URL: for a
  // brand-new email it routes through the MSG91 mobile gate at
  // /auth/session/complete (same code path as email/password signup),
  // and for a returning email it creates the session directly. We use
  // the exact same handler as Login.jsx so the two screens are in sync.
  //
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
  // URLS — THIS BREAKS THE AUTH.
  const handleGoogle = () => {
    // Iter-52.27 — stash the form fields BEFORE we hand off to
    // Google, so that after the round-trip AuthCallback.jsx can
    // forward name/city/state to /auth/session/complete. Without
    // this, every Google signup ended up with empty geo + name
    // (fell back to whatever Google returned).
    try {
      const stash = {
        name: (name || '').trim(),
        city: locationSkipped ? '' : (city || '').trim(),
        state: locationSkipped ? '' : (stateName || ''),
      };
      window.sessionStorage.setItem('rb_google_signup_stash', JSON.stringify(stash));
    } catch { /* sessionStorage disabled — Google fallback still works, just without geo */ }
    const redirectUrl = window.location.origin + '/profiles';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onWidgetVerified = async (accessToken, widgetMobile) => {
    // Parent-side single-fire guard. Widget also guards, but auth
    // deserves belt AND braces.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setStep('submitting');
    setError('');
    setDupMobile(false);
    // Iter-52.26 — funnel step 2: OTP was submitted & MSG91 confirmed
    // it. Fire BEFORE the register POST so an error during account
    // creation still leaves the "otp_submitted success" row on record.
    // Iter-52.31 — include the mobile MSG91 verified so the funnel
    // drilldown shows the number even before the account exists.
    otpSubmittedFiredRef.current = true;
    widgetOpenRef.current = false;
    emitSignupEvent('otp_submitted', { mode, status: 'success', mobile_e164: widgetMobile || widgetMobileRef.current || undefined });
    try {
      // Iter-52.23 — phone-only mode omits email + password so the
      // backend creates a mobile-identified user with no email.
      // Iter-52.25 — additionally send name (now REQUIRED),
      // city+state (optional if user tapped Skip), and
      // first-touch attribution (auto-captured on app boot).
      const attribution = getAttribution() || undefined;
      const shared = {
        name: name.trim(),
        city: locationSkipped ? undefined : city.trim() || undefined,
        state: locationSkipped ? undefined : stateName || undefined,
        attribution,
        access_token: accessToken,
      };
      const payload = mode === 'phone_only'
        ? shared
        : { email: email.trim(), password, ...shared };
      const { data } = await api.post('/auth/register-with-otp', payload);
      // Iter-52.26 — funnel step 3: account created. Reset the
      // session ID so a returning user starts a fresh funnel.
      emitSignupEvent('signup_completed', {
        mode,
        user_id: data?.user_id,
        mobile_e164: data?.mobile_e164,
      });
      clearSignupSessionId();
      // Iter-51.24f — the session cookie is set, but AppContext.user
      // is still null (it was fetched pre-registration). Refresh the
      // auth state BEFORE navigating, otherwise /profiles bounces
      // back to the landing "Sign In" page and the user thinks the
      // account didn't get created (silent-redirect complaint #2).
      try { await checkAuth(); } catch { /* /auth/me will retry on next mount */ }
      // Show an explicit success card for ~1.6 s so the user sees a
      // clear confirmation, then navigate to profile setup.
      setStep('success');
      toast.success('Account created — welcome to ReadBuddy!');
      setTimeout(() => nav('/profiles', { replace: true }), 1600);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Could not create your account. Please try again.';
      if (status === 409 && /free trial/i.test(detail)) setDupMobile(true);
      let uiMsg;
      // Iter-51.20 — explicit hint when someone chose Email in the MSG91
      // popup.
      if (status === 400 && /mobile/i.test(detail) && /email/i.test(detail)) {
        uiMsg = 'Please choose "Mobile" (not Email) at the top of the OTP popup, then re-enter your number.';
      } else {
        uiMsg = detail;
      }
      setError(uiMsg);
      // Iter-51.24f — DO NOT auto-remount the widget on error. The
      // previous flow bumped widgetKey which re-opened MSG91's popup
      // and covered the error message before the user could read it
      // (flashing-error complaint #1). Instead, park on a dedicated
      // 'blocked' step that shows the error persistently with an
      // explicit "Try again" button — the button remounts the widget
      // only when the user is ready.
      // Iter-51.24g — the error toast is now STICKY (duration:
      // Infinity). The user reported the toast auto-dismissing after
      // ~8s was being interpreted as "the error disappeared", even
      // though the inline red card kept rendering. Making it sticky
      // + closeButton means the user MUST tap × or take action —
      // no timer, no fade, no auto-clear anywhere.
      toast.error(uiMsg, { duration: Infinity, id: 'register-error' });
      submittingRef.current = false;
      setStep('blocked');
    }
  };

  const onWidgetError = (msg, widgetMobile) => {
    // Widget-level failure (network / MSG91 cancelled / SDK didn't
    // load). Widget shows its own error state; the parent surfaces a
    // secondary message ONLY when we're not currently submitting to
    // the backend — otherwise the parent's own status wins.
    // Iter-52.26 — funnel step 2 (failure branch): OTP entry failed.
    // Fired only when the parent is not mid-submit, so we don't
    // double-count a success that happened moments earlier.
    // Iter-52.31 — carry the mobile the user was trying (read from
    // the widget's shadow-DOM input) so the drilldown row shows the
    // number and the Nudge button becomes tappable for drop-offs.
    if (!submittingRef.current) {
      otpSubmittedFiredRef.current = true;
      emitSignupEvent('otp_submitted', {
        mode, status: 'failed',
        mobile_e164: widgetMobile || widgetMobileRef.current || undefined,
        error: (msg || '').slice(0, 200),
      });
      setError(msg || 'Mobile verification failed. Please try again.');
    }
  };

  // Iter-52.32 — cache the mobile MSG91's shadow-DOM shows as it's
  // typed. See widgetMobileRef comment at top of component.
  const onWidgetMobileTyped = (mobile) => {
    if (mobile) widgetMobileRef.current = mobile;
  };

  // Iter-51.24f — dedicated success screen. Shown for ~1.6s before we
  // navigate to /profiles, giving the user an explicit confirmation
  // that the account was created (previously silently redirected).
  if (step === 'success') {
    return (
      <AuthShell>
        <div data-testid="register-success" className="text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
            <ShieldCheck size={40} strokeWidth={2.5} className="text-emerald-600"/>
          </div>
          <h1 className="text-2xl font-extrabold text-[#2B2140] mb-1">Account created!</h1>
          <p className="text-sm text-slate-600 mb-6">
            Welcome, {name.split(' ')[0] || 'there'}. Setting up your profile in a moment…
          </p>
          <div className="inline-flex items-center gap-2 text-sm font-bold text-emerald-700">
            <Loader2 size={16} className="animate-spin"/> One second…
          </div>
        </div>
      </AuthShell>
    );
  }

  // Iter-51.24f — dedicated "blocked" screen. Reached when the server
  // rejected the registration (duplicate email, duplicate mobile,
  // MSG91 rejection, etc). The previous flow bumped widgetKey on
  // error, which re-opened the MSG91 popup and hid the error message
  // behind it. Now we stay on this static screen until the user
  // explicitly taps "Try again" (which remounts the widget).
  // Iter-52.32 — user backed out of the widget without completing.
  // Same intent as a pagehide beacon: emit an abandoned funnel event
  // with the cached mobile so the drop-off shows up in admin with a
  // tappable Nudge button. Guarded so pagehide doesn't double-count.
  const _abandonWidget = () => {
    if (widgetOpenRef.current && !otpSubmittedFiredRef.current) {
      otpSubmittedFiredRef.current = true;
      emitSignupEvent('otp_submitted', {
        mode,
        status: 'failed',
        mobile_e164: widgetMobileRef.current || undefined,
        error: 'abandoned',
      });
    }
    widgetOpenRef.current = false;
  };

  if (step === 'blocked') {
    return (
      <AuthShell>
        <button
          data-testid="register-back-to-form"
          onClick={() => {
            _abandonWidget();
            setStep('form');
            setError('');
            setDupMobile(false);
            setWidgetKey(k => k + 1);
            submittingRef.current = false;
            toast.dismiss('register-error');
          }}
          className="text-sm font-bold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14}/> Back to sign-up form
        </button>
        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full bg-rose-100 mx-auto mb-3 flex items-center justify-center">
            <RefreshCw size={30} strokeWidth={2.5} className="text-rose-600"/>
          </div>
          <h1 className="text-2xl font-extrabold text-[#2B2140]">We couldn't create your account</h1>
        </div>
        <div data-testid="register-blocked-error"
          className="rounded-2xl bg-rose-50 border-2 border-rose-200 text-rose-800 text-sm font-semibold px-4 py-3 mb-4 leading-relaxed">
          {error}
        </div>
        {dupMobile ? (
          <a
            data-testid="register-dup-mobile-whatsapp"
            href={`https://wa.me/919599668449?text=${encodeURIComponent(`Hi Leena, my mobile already used a ReadBuddy trial. I'd like to subscribe.`)}`}
            target="_blank" rel="noopener noreferrer"
            className="pushable w-full bg-[#25D366] text-white text-sm font-bold py-3 rounded-full inline-flex items-center justify-center gap-2"
          >
            <MessageCircle size={14} strokeWidth={2.6}/> Subscribe on WhatsApp
          </a>
        ) : (
          <button
            data-testid="register-blocked-retry"
            onClick={() => {
              setError('');
              setWidgetKey(k => k + 1);
              submittingRef.current = false;
              setHintAcked(false);   // iter-52.41 — re-show hint on retry
              setStep('widget');
              toast.dismiss('register-error');
            }}
            className="pushable w-full bg-[#FF6B1A] text-white text-sm font-bold py-3 rounded-full inline-flex items-center justify-center gap-2"
          >
            <RefreshCw size={14}/> Try again with a fresh OTP
          </button>
        )}
        {/^.*email.*(exists|registered).*$/i.test(error) && (
          <Link
            data-testid="register-blocked-signin-link"
            to="/"
            className="pushable mt-3 w-full bg-slate-100 text-slate-700 text-sm font-bold py-2.5 rounded-full inline-flex items-center justify-center gap-2"
          >
            Sign in instead
          </Link>
        )}

        {/* Iter-51.24h — Verification tips collapsible. Explains the
            common failure reasons so a parent can self-diagnose
            without contacting support. Collapsed by default so it
            doesn't clutter the primary Retry / Subscribe action;
            expands to a labelled list of the three most-reported
            causes with a suggested next step for each. */}
        <button
          type="button"
          data-testid="register-tips-toggle"
          onClick={() => setTipsOpen(v => !v)}
          aria-expanded={tipsOpen}
          className="mt-5 w-full text-left inline-flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-bold"
        >
          <span className="inline-flex items-center gap-1.5">
            <Info size={14}/> Why did this happen? — verification tips
          </span>
          {tipsOpen ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
        </button>
        {tipsOpen && (
          <div data-testid="register-tips-panel" className="mt-2 rounded-xl border border-slate-200 bg-white p-3 text-[13px] text-slate-700 leading-relaxed">
            <p className="font-bold text-slate-800 mb-2">Three most common reasons:</p>
            <ul className="space-y-2 list-none">
              <li className="flex gap-2">
                <span className="text-[#FF6B1A] font-extrabold shrink-0">1.</span>
                <span>
                  <span className="font-bold">The email is already registered.</span> If you or a family member signed up before with this email, tap <span className="font-bold">Sign in instead</span> above and use "Forgot password" if you don't remember it.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#FF6B1A] font-extrabold shrink-0">2.</span>
                <span>
                  <span className="font-bold">The mobile number was already used for a free trial.</span> Only one free trial per mobile is allowed. Tap <span className="font-bold">Subscribe on WhatsApp</span> if you'd like to continue on a paid plan.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#FF6B1A] font-extrabold shrink-0">3.</span>
                <span>
                  <span className="font-bold">You picked "Email" instead of "Mobile" in the OTP popup.</span> When the popup opens, make sure the top tab says <span className="font-bold">Mobile</span> (not Email) — we can only verify mobile numbers for now.
                </span>
              </li>
            </ul>
            <p className="mt-3 text-[11px] text-slate-500">
              Still stuck? <a
                href={`https://wa.me/919599668449?text=${encodeURIComponent(`Hi Leena, I'm having trouble creating my ReadBuddy account. The error was: ${error}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="underline font-bold text-emerald-700"
              >WhatsApp Leena</a> and we'll get you sorted.
            </p>
          </div>
        )}
      </AuthShell>
    );
  }

  if (step === 'widget' || step === 'submitting') {
    const submitting = step === 'submitting';
    return (
      <AuthShell>
        <button
          data-testid="register-back-to-form"
          onClick={() => {
            if (submitting) return;   // don't allow cancel mid-submit
            _abandonWidget();
            setStep('form');
            setError('');
            setDupMobile(false);
            setWidgetKey(k => k + 1);
            submittingRef.current = false;
          }}
          disabled={submitting}
          className="text-sm font-bold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4 disabled:opacity-40"
        >
          <ArrowLeft size={14}/> Back
        </button>
        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-3 flex items-center justify-center">
            {submitting
              ? <Loader2 size={30} strokeWidth={2.5} className="text-emerald-600 animate-spin"/>
              : <ShieldCheck size={30} strokeWidth={2.5} className="text-emerald-600"/>}
          </div>
          <h1 data-testid="register-widget-heading" className="text-2xl font-extrabold text-[#2B2140]">
            {submitting ? 'Creating your account…' : 'Verify your mobile'}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {submitting
              ? 'Confirming with MSG91 and setting up your ReadBuddy account.'
              : 'Enter your mobile number in the popup — we\'ll send a 4-digit OTP to confirm.'}
          </p>
        </div>

        {/* Iter-52.39 — bilingual WhatsApp hint. Rendered ABOVE the
            widget (not inside — MSG91's shadow-DOM popup can't be
            reached to inject helper text) so parents are reminded to
            enter a number that's actually on WhatsApp. Nudges,
            welcome-messages and the future automated 3-day silence
            reminder are all WhatsApp-first, so a non-WhatsApp
            number silently loses outreach reach. */}
        <div
          data-testid="whatsapp-hint"
          className="mb-3 px-4 py-3 rounded-2xl bg-[#25D366]/10 border-2 border-[#25D366]/40 text-[14px] text-[#0F5132] leading-snug"
        >
          <div className="font-extrabold flex items-start gap-2">
            <span className="text-lg leading-none">📱</span>
            <span>Please enter your <span className="underline decoration-2 decoration-[#25D366] underline-offset-2">WhatsApp number</span></span>
          </div>
          <div className="text-[12px] text-[#0F5132]/85 font-hi mt-1 ml-6">कृपया अपना WhatsApp नंबर दर्ज करें</div>
          <div className="text-[11px] text-[#0F5132]/70 mt-1.5 ml-6">
            We'll send OTP + trial reminders on WhatsApp.
          </div>
        </div>

        {/* Iter-52.41 — Gate the MSG91 widget mount behind an explicit
            "Verify my WhatsApp number" ack. Rationale: MSG91's SDK
            auto-opens a full-screen popup the moment its custom
            element mounts, so if we render the widget on step-enter
            the popup instantly covers our hint. Requiring the user
            to tap this button keeps the hint on-screen until they've
            actively read it. When they close MSG91's popup we return
            to this button so they can re-launch without losing the
            hint. */}
        {!hintAcked && !submitting && (
          <button
            data-testid="whatsapp-hint-ack"
            onClick={() => setHintAcked(true)}
            className="pushable w-full bg-[#25D366] hover:bg-[#20BF5B] text-white text-base font-bold py-3 rounded-full inline-flex items-center justify-center gap-2"
          >
            I got it — verify my WhatsApp number
          </button>
        )}

        {/* Widget stays in-tree during submitting so its SDK state
            doesn't get destroyed, but visually it's hidden by the
            spinner overlay above the ONE status message. */}
        {(hintAcked || submitting) && (
          <div className={submitting ? 'pointer-events-none opacity-40' : ''}>
            <Msg91Widget
              key={widgetKey}
              onVerified={onWidgetVerified}
              onError={onWidgetError}
              onMobileTyped={onWidgetMobileTyped}
              label="Continue in MSG91 popup"
            />
          </div>
        )}

        {/* ONE status message — either the parent's error, or the
            submitting hint. Widget's own state is deliberately mute
            (no 'verified' state) so this is the only source of truth. */}
        {!submitting && error && (
          <p data-testid="register-widget-error" className="text-sm text-rose-600 mt-4 text-center">
            {error}
          </p>
        )}
        {!submitting && !error && (
          <p data-testid="register-widget-hint" className="text-xs text-slate-500 mt-4 text-center">
            Your number will only be used for verification and account recovery.
          </p>
        )}
        {submitting && (
          <p data-testid="register-submitting" className="text-sm text-emerald-700 text-center mt-4 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin"/> Almost done — creating your account…
          </p>
        )}
        {!submitting && error && dupMobile && (
          <a
            data-testid="register-dup-mobile-whatsapp"
            href={`https://wa.me/919599668449?text=${encodeURIComponent(`Hi Leena, my mobile already used a ReadBuddy trial. I'd like to subscribe.`)}`}
            target="_blank" rel="noopener noreferrer"
            className="pushable mt-4 w-full bg-[#25D366] text-white text-sm font-bold py-2.5 rounded-full inline-flex items-center justify-center gap-2"
          >
            <MessageCircle size={14} strokeWidth={2.6}/> Subscribe on WhatsApp
          </a>
        )}
        {!submitting && error && !dupMobile && (
          <button
            data-testid="register-retry-widget"
            onClick={() => { setError(''); setWidgetKey(k => k + 1); submittingRef.current = false; setHintAcked(false); }}
            className="pushable mt-3 w-full bg-slate-100 text-slate-700 text-sm font-bold py-2.5 rounded-full inline-flex items-center justify-center gap-2"
          >
            <RefreshCw size={14}/> Try again with a fresh OTP
          </button>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <button onClick={() => nav('/login')} data-testid="register-back-btn"
        className="text-sm font-bold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14}/> Back
      </button>
      <h1 className="text-3xl font-extrabold text-[#2B2140] mb-1">Create your account</h1>
      <p className="text-sm text-slate-500 mb-6">
        We'll verify your mobile with a 4-digit OTP before starting your free trial.
      </p>
      <form onSubmit={submitStep1} className="space-y-3">
        <Field icon={<User size={18}/>}>
          <input data-testid="register-name" required placeholder="Your name" value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 bg-transparent outline-none py-3 text-[#2B2140]"/>
        </Field>
        {/* Iter-52.25 — City + State so we can attribute signups to
            geo for Facebook-ad targeting. Collapsed behind a "Skip for
            now" link when the parent is in a hurry — Name is still
            required, only geo is optional. */}
        {!locationSkipped ? (
          <>
            <Field icon={<MapPin size={18}/>}>
              <select data-testid="register-state" required value={stateName}
                onChange={(e) => setStateName(e.target.value)}
                className="flex-1 bg-transparent outline-none py-3 text-[#2B2140]">
                <option value="">Select your state</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field icon={<Building2 size={18}/>}>
              <input data-testid="register-city" required placeholder="Your city" value={city}
                onChange={(e) => setCity(e.target.value)}
                className="flex-1 bg-transparent outline-none py-3 text-[#2B2140]"/>
            </Field>
            <button type="button" data-testid="register-skip-location"
              onClick={() => { setLocationSkipped(true); setStateName(''); setCity(''); }}
              className="text-[11px] font-bold text-slate-500 hover:text-slate-800 underline">
              Skip city &amp; state for now
            </button>
          </>
        ) : (
          <button type="button" data-testid="register-add-location"
            onClick={() => setLocationSkipped(false)}
            className="text-[11px] font-bold text-[#FF6B1A] hover:underline inline-flex items-center gap-1">
            <MapPin size={12}/> Add your city &amp; state (optional)
          </button>
        )}
        <Field icon={<Mail size={18}/>}>
          <input data-testid="register-email" type="email" required placeholder="Email address"
            value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
            className="flex-1 bg-transparent outline-none py-3 text-[#2B2140]"/>
        </Field>
        <label className="relative flex items-center gap-3 pl-4 pr-14 rounded-2xl border-2 border-slate-200 bg-white focus-within:border-[#FF6B1A]">
          <Lock size={18} className="text-slate-400"/>
          <input data-testid="register-password" type={showPassword ? 'text' : 'password'} required minLength={8}
            placeholder="Password (min 8 characters)"
            value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
            className="flex-1 min-w-0 bg-transparent outline-none py-3 text-[#2B2140]"/>
          <button data-testid="register-password-toggle" type="button" onClick={() => setShowPassword(v => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute right-1 top-1/2 -translate-y-1/2 min-w-11 min-h-11 flex items-center justify-center text-slate-600 hover:text-[#2B2140]">
            {showPassword ? <EyeOff className="w-[1.5em] h-[1.5em]"/> : <Eye className="w-[1.5em] h-[1.5em]"/>}
          </button>
        </label>
        {error && <p data-testid="register-error" className="text-sm text-rose-600">{error}</p>}
        <button data-testid="register-submit-btn" type="submit"
          className="pushable w-full bg-[#FF6B1A] text-white text-lg font-bold py-3 rounded-full inline-flex items-center justify-center gap-2">
          Continue to mobile verification
        </button>
      </form>
      <p className="text-xs text-center text-slate-500 mt-6">
        Already have an account?{' '}
        <Link data-testid="register-signin-link" to="/login" className="font-bold text-[#5B8DEF]">Sign in</Link>
      </p>

      {/* Iter-52.5 — Sign-up-with-Google. Matches Login.jsx layout
          (email form above, OR divider, Google button below) so the
          two auth screens feel identical to the user. Same handler,
          same underlying auth.emergentagent.com endpoint. */}
      <div className="flex items-center gap-3 my-5 w-full" data-testid="register-google-divider">
        <div className="h-px bg-slate-300/70 flex-1"/>
        <span className="text-xs font-bold uppercase text-slate-500 tracking-widest">or</span>
        <div className="h-px bg-slate-300/70 flex-1"/>
      </div>
      <button
        type="button"
        data-testid="google-signup-btn"
        onClick={handleGoogle}
        className="pushable pushable-primary w-full bg-white border-2 border-[#2B2140] text-[#2B2140] text-base font-bold px-8 py-3 rounded-full inline-flex items-center justify-center gap-3"
      >
        Sign up with Google
      </button>
      {/* Iter-52.23 — phone-only signup path. No email required, no
          password. Shown only when the server flag is on (kill switch
          for prod without a rebuild). Deliberately placed BELOW the
          classic form + Google button so it doesn't compete with the
          default flow — reachable in one tap for parents who have
          no email address. */}
      {phoneOnlyEnabled && (
        <button
          type="button"
          data-testid="phone-only-signup-btn"
          onClick={startPhoneOnly}
          className="pushable mt-3 w-full bg-[#25D366] text-white text-base font-bold px-8 py-3 rounded-full inline-flex items-center justify-center gap-2"
        >
          <Phone size={18} strokeWidth={2.6}/> Sign up with phone only (no email)
        </button>
      )}
      {/* Iter-52.55.13 — tiny build-version stamp so a customer
          screenshot instantly reveals whether their phone is running
          the latest bundle or a stale cached one. */}
      <p data-testid="register-build-version"
        className="mt-6 text-center text-[10px] tracking-wider text-slate-400 font-mono select-all">
        v{BUILD_VERSION}
      </p>
    </AuthShell>
  );
}

function Field({ icon, children }) {
  return (
    <label className="flex items-center gap-3 px-4 rounded-2xl border-2 border-slate-200 bg-white focus-within:border-[#FF6B1A]">
      <span className="text-slate-400">{icon}</span>
      {children}
    </label>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F4B740] blur-3xl opacity-70"/>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#D97398] blur-3xl opacity-70"/>
      <div className="relative z-10 w-full max-w-md bg-white/95 backdrop-blur rounded-3xl p-8 shadow-xl">
        {children}
      </div>
    </div>
  );
}
